import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { resolveAssetUrl } from "../../lib/api";
import { Spinner } from "../../components/Brand";
import { currency } from "../../lib/status";
import { useSEO } from "../../lib/seo";

export default function CustomerHome() {
    useSEO({
        title: "Book Home Services in UAE — AC, Plumbing, Cleaning & more | Luxurio Edge",
        description: "Browse 12+ on-demand home service categories. Vetted technicians, transparent pricing in AED, OTP-secure completion. Available across Dubai, Abu Dhabi, Sharjah and Ajman.",
    });
    const [cats, setCats] = useState(null);
    const [allServices, setAllServices] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const q = (searchParams.get("q") || "").trim();
    const catSlug = (searchParams.get("cat") || "").trim();
    const sort = searchParams.get("sort") || "popular";
    const pricingFilter = searchParams.get("pricing") || "";

    useEffect(() => {
        (async () => {
            const [c, s] = await Promise.all([
                api.get("/categories"),
                api.get("/services"),
            ]);
            setCats(c.data);
            setAllServices(s.data);
        })();
    }, []);

    // ETA = min duration across services in the category
    const etaByCat = useMemo(() => {
        const map = {};
        for (const s of allServices) {
            const cur = map[s.category_id];
            if (cur === undefined || s.duration_minutes < cur) {
                map[s.category_id] = s.duration_minutes;
            }
        }
        return map;
    }, [allServices]);

    const results = useMemo(() => {
        if (!q && !catSlug) return null;
        const lower = q.toLowerCase();
        const cat = catSlug ? cats?.find((c) => c.slug === catSlug) : null;
        let list = allServices.filter((s) => {
            if (cat && s.category_id !== cat.id) return false;
            if (q && !s.name.toLowerCase().includes(lower) && !(s.description || "").toLowerCase().includes(lower)) return false;
            if (pricingFilter && s.pricing_type !== pricingFilter) return false;
            return true;
        });
        if (sort === "price_asc") list = [...list].sort((a, b) => a.base_price - b.base_price);
        else if (sort === "price_desc") list = [...list].sort((a, b) => b.base_price - a.base_price);
        else if (sort === "fastest") list = [...list].sort((a, b) => a.duration_minutes - b.duration_minutes);
        return list;
    }, [q, catSlug, allServices, cats, sort, pricingFilter]);

    const activeCat = catSlug ? cats?.find((c) => c.slug === catSlug) : null;
    const updateParam = (key, val) => {
        const next = new URLSearchParams(searchParams);
        if (val) next.set(key, val);
        else next.delete(key);
        setSearchParams(next);
    };

    if (!cats) return <Spinner />;

    // 8 primary tiles in the highlighted card-grid, rest below
    const top = cats.slice(0, 8);
    const rest = cats.slice(8);

    // Smart bundles — derived from existing services tagged as "Annual" / "Contract" / multi-hour
    const bundles = allServices.filter((s) =>
        /annual|contract|deep|cctv|install/i.test(s.name),
    ).slice(0, 4);

    return (
        <div className="space-y-12 lx-fade-up">
            {q || catSlug ? (
                <section className="space-y-6">
                    <button
                        onClick={() => setSearchParams({})}
                        data-testid="back-to-home-btn"
                        className="text-sm text-[#555] hover:text-[#180A21]"
                    >
                        ← Back to home
                    </button>

                    {/* Category hero (when filtering by category) */}
                    {activeCat ? (
                        <div className="relative overflow-hidden h-44 sm:h-56">
                            {activeCat.image_url && (
                                <img
                                    src={resolveAssetUrl(activeCat.image_url)}
                                    alt={activeCat.name}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-r from-[#180A21]/95 via-[#180A21]/70 to-transparent" />
                            <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
                                <div className="lx-label text-[#D4AF37] mb-2">Category</div>
                                <h1 className="font-sans font-bold text-3xl sm:text-4xl text-white tracking-tight">
                                    {activeCat.name}
                                </h1>
                                <div className="text-sm text-white/70 mt-2">
                                    {results.length} services available
                                </div>
                            </div>
                        </div>
                    ) : (
                        <h1 className="font-sans font-bold text-3xl sm:text-4xl text-[#0E0E0E] tracking-tight">
                            Results for <span className="lx-gold-text font-serif italic font-normal">"{q}"</span>
                        </h1>
                    )}

                    {/* Filters bar */}
                    <div className="flex flex-wrap items-center gap-3 pb-2 border-b border-[#E5E2DC]">
                        <span className="lx-label text-[#555] mr-1">Sort</span>
                        {[
                            ["popular", "Popular"],
                            ["price_asc", "Price ↑"],
                            ["price_desc", "Price ↓"],
                            ["fastest", "Fastest"],
                        ].map(([v, l]) => (
                            <button
                                key={v}
                                data-testid={`sort-${v}`}
                                onClick={() => updateParam("sort", v === "popular" ? "" : v)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition ${
                                    (sort === v || (v === "popular" && !sort))
                                        ? "bg-[#180A21] text-[#D4AF37] border-[#180A21]"
                                        : "border-[#E5E2DC] text-[#555] hover:border-[#D4AF37]/50"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                        <div className="w-px h-5 bg-[#E5E2DC] mx-1" />
                        <span className="lx-label text-[#555] mr-1">Pricing</span>
                        {[
                            ["", "All"],
                            ["fixed", "Fixed"],
                            ["hourly", "Hourly"],
                            ["inspection", "Inspection"],
                        ].map(([v, l]) => (
                            <button
                                key={l}
                                data-testid={`pricing-${l.toLowerCase()}`}
                                onClick={() => updateParam("pricing", v)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition ${
                                    pricingFilter === v
                                        ? "bg-[#D4AF37] text-[#180A21] border-[#D4AF37]"
                                        : "border-[#E5E2DC] text-[#555] hover:border-[#D4AF37]/50"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    {results.length === 0 ? (
                        <div className="text-center py-16 text-[#555]">
                            No services match your filters.
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {results.map((s) => (
                                <ServiceCard key={s.id} s={s} />
                            ))}
                        </div>
                    )}
                </section>
            ) : (
                <>
                    {/* HERO — Big bold headline left, photo collage right */}
                    <section className="grid lg:grid-cols-12 gap-8 items-start pt-2">
                        <div className="lg:col-span-5 lg:pt-12">
                            <h1
                                className="font-sans font-bold text-[#0E0E0E] leading-[1.05] tracking-[-0.02em]"
                                style={{ fontSize: "clamp(2.5rem, 5vw, 4.25rem)" }}
                                data-testid="cust-hero-heading"
                            >
                                Home services
                                <br />
                                at your <span className="lx-gold-text font-serif italic font-normal">doorstep</span>
                            </h1>
                            <p className="text-[#555] mt-6 max-w-md leading-relaxed text-base">
                                Vetted technicians, premium tools, transparent pricing —
                                booked in 60 seconds.
                            </p>

                            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
                                <Highlight value="12K+" label="Verified pros" />
                                <Highlight value="4.9★" label="Avg rating" />
                                <Highlight value="34" label="Cities" />
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="grid grid-cols-2 grid-rows-2 gap-2.5 h-[460px] sm:h-[520px]">
                                <CollageTile
                                    img="https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=1200&q=80"
                                    label="Home Automation"
                                    className="row-span-2"
                                />
                                <CollageTile
                                    img="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=900&q=80"
                                    label="Appliance Repair"
                                />
                                <CollageTile
                                    img="https://static.prod-images.emergentagent.com/jobs/49d685dd-4570-4cf1-ba99-898459f22c0a/images/db03c7614967f04752bf0b5b63dfaa60b6ff67d57cc91c2d98bdcecf57a52e83.png"
                                    label="AC & Appliances"
                                />
                            </div>
                        </div>
                    </section>

                    {/* CATEGORIES — card grid with icon-style tiles + green ETA pill */}
                    <section>
                        <div className="bg-[#FAFAF8] border border-[#F0EEE8] p-5 sm:p-7">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
                                {top.map((c) => (
                                    <CategoryTile
                                        key={c.id}
                                        c={c}
                                        eta={etaByCat[c.id]}
                                    />
                                ))}
                            </div>
                            {rest.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 mt-4">
                                    {rest.map((c) => (
                                        <CategoryTile
                                            key={c.id}
                                            c={c}
                                            eta={etaByCat[c.id]}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* LUXURIO SMART BUNDLES — equivalent of "Native Smart Products" */}
                    {bundles.length > 0 && (
                        <section>
                            <div className="flex items-end justify-between mb-5">
                                <div>
                                    <div className="lx-label text-[#B2912A] mb-2">
                                        Luxurio Smart Bundles
                                    </div>
                                    <h2 className="font-sans font-bold text-3xl sm:text-4xl text-[#0E0E0E] tracking-tight">
                                        Curated packages
                                    </h2>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {bundles.map((s) => (
                                    <BundleCard key={s.id} s={s} />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* MOST BOOKED — large card grid */}
                    <section>
                        <div className="flex items-end justify-between mb-5">
                            <div>
                                <div className="lx-label text-[#B2912A] mb-2">
                                    Featured Services
                                </div>
                                <h2 className="font-sans font-bold text-3xl sm:text-4xl text-[#0E0E0E] tracking-tight">
                                    Most-booked this week
                                </h2>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                            {allServices.slice(0, 8).map((s) => (
                                <ServiceCard key={s.id} s={s} compact />
                            ))}
                        </div>
                    </section>

                    {/* Why us */}
                    <section className="bg-[#180A21] text-white p-8 lg:p-12 grid md:grid-cols-3 gap-8">
                        <Why
                            title="Background-checked"
                            body="Every technician is vetted, certified and trained on Luxurio standards."
                        />
                        <Why
                            title="Transparent pricing"
                            body="No hidden surcharges. See your final bill before the technician arrives."
                        />
                        <Why
                            title="100% satisfaction"
                            body="Free re-visit within 14 days if anything isn't right. Concierge support always."
                        />
                    </section>
                </>
            )}
        </div>
    );
}

function CategoryTile({ c, eta }) {
    return (
        <Link
            to={`/customer?cat=${c.slug}`}
            data-testid={`cat-tile-${c.slug}`}
            className="group block text-center"
        >
            <div className="relative overflow-hidden bg-white border border-[#F0EEE8] group-hover:border-[#D4AF37] transition-all aspect-square">
                {c.image_url && (
                    <img
                        src={resolveAssetUrl(c.image_url)}
                        alt={c.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                )}
                {eta && (
                    <div
                        className="absolute top-2 left-2 bg-white px-2 py-0.5 text-[10px] font-bold tracking-wide"
                        style={{
                            color: "#1A8A3A",
                            border: "1px solid #1A8A3A",
                            borderRadius: "2px",
                        }}
                    >
                        {eta} mins
                    </div>
                )}
            </div>
            <div className="mt-3 px-1 text-[13px] sm:text-sm text-[#0E0E0E] font-medium leading-tight group-hover:text-[#D4AF37] transition-colors">
                {c.name}
            </div>
        </Link>
    );
}

function ServiceCard({ s, compact }) {
    return (
        <Link
            to={`/customer/services/${s.id}`}
            data-testid={`service-card-${s.id}`}
            className="lx-card group block overflow-hidden"
        >
            <div className={`overflow-hidden bg-[#F3F2F0] ${compact ? "aspect-[4/3]" : "aspect-[16/10]"}`}>
                {s.image_url && (
                    <img
                        src={resolveAssetUrl(s.image_url)}
                        alt={s.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                )}
            </div>
            <div className={compact ? "p-4" : "p-6"}>
                <div className="lx-label text-[#B2912A] mb-1.5">
                    {s.duration_minutes} min · {s.pricing_type}
                </div>
                <h3 className={`font-sans font-semibold text-[#0E0E0E] leading-tight ${compact ? "text-base" : "text-lg"}`}>
                    {s.name}
                </h3>
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-[#F3F2F0]">
                    <div className={`font-serif text-[#180A21] ${compact ? "text-lg" : "text-xl"}`}>
                        {currency(s.base_price, "AED")}
                    </div>
                    <div className="text-[#D4AF37] text-[10px] tracking-wider uppercase font-medium">
                        Book →
                    </div>
                </div>
            </div>
        </Link>
    );
}

function BundleCard({ s }) {
    return (
        <Link
            to={`/customer/services/${s.id}`}
            data-testid={`bundle-${s.id}`}
            className="group block bg-white border border-[#F0EEE8] hover:border-[#D4AF37] transition overflow-hidden"
        >
            <div className="aspect-[4/3] overflow-hidden bg-[#FAFAF8]">
                {s.image_url && (
                    <img
                        src={resolveAssetUrl(s.image_url)}
                        alt={s.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                )}
            </div>
            <div className="p-4">
                <div className="lx-label text-[#B2912A] mb-1">Bundle</div>
                <div className="font-sans font-semibold text-[#0E0E0E] text-sm leading-tight mb-2">
                    {s.name}
                </div>
                <div className="font-serif text-lg text-[#180A21]">
                    {currency(s.base_price, "AED")}
                </div>
            </div>
        </Link>
    );
}

function CollageTile({ img, label, className = "" }) {
    return (
        <div className={`relative overflow-hidden group ${className}`}>
            <img
                src={img}
                alt={label}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#180A21]/70 via-[#180A21]/10 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
                <div className="lx-label text-[#D4AF37]">{label}</div>
            </div>
        </div>
    );
}

function Highlight({ value, label }) {
    return (
        <div>
            <div className="font-sans font-bold text-2xl text-[#0E0E0E]">{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#555] mt-1">
                {label}
            </div>
        </div>
    );
}

function Why({ title, body }) {
    return (
        <div>
            <div className="lx-label text-[#D4AF37] mb-2">{title}</div>
            <p className="text-sm text-white/70 leading-relaxed">{body}</p>
        </div>
    );
}
